import { Injectable, Optional } from '@angular/core';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar, MatSnackBarRef } from '@angular/material/snack-bar';
import { Observable, Subscription, filter } from 'rxjs';

/**
 * Why {@link OverlayTeardownService.closeAll} was called. Decides ONE thing: whether the
 * {@link OverlayTeardownService.exemptFromTeardown} <b>dialog</b> opt-out is honoured — yes on
 * {@code 'session-teardown'} (same user, still at the screen), no on {@code 'identity-switch'}
 * (tab handed to a different user, so one user's one-time secrets must not stay on another's
 * screen). The <b>snackbar</b> opt-out
 * ({@link OverlayTeardownService.exemptSnackBarFromTeardown}) is reason-independent — a toast holds
 * no secret — and is one-shot instead.
 */
export type OverlayCloseReason = 'session-teardown' | 'identity-switch';

/**
 * Closes every open CDK overlay (dialogs, snackbar, bottom sheet) on session teardown and on a
 * cross-tab identity switch. Exists because the router cannot: the overlay container is a
 * {@code <body>} sibling of {@code <app-root>}, so navigating to /auth leaves it painted over the
 * login page — and most dialogs here are {@code disableClose}, so the user's only escape was a
 * reload.
 *
 * <p>See <b>"Session teardown closes the overlays (the router can't)"</b> in CLAUDE.md for the full
 * rules — the exemption policy, the {@code afterUserClosed} contract, and the identity-switch
 * behaviour. Comments here cover only what is not derivable from the code.
 *
 * <p>The three dependencies are {@code @Optional()} because each is provided by its own Material
 * module, not the root injector: a TestBed that doesn't import them would otherwise throw
 * NullInjectorError the moment it injects AuthService/AuthSyncService (dozens of specs do). At
 * runtime AppModule imports MaterialModule, so the null branches only happen in tests — hence the
 * {@link warnIfDepsMissing} tripwire. Dismissal goes through MatSnackBar rather than the
 * AppSnackBarService proxy because that proxy's own MatSnackBar dependency is required, which would
 * defeat the optionality; only OPENING a toast needs the duration preset.
 *
 * <p><b>One root instance covers dialogs opened from lazy modules</b>, because Material's
 * {@code openDialogs} getter returns the PARENT's list whenever a parent exists (MatSnackBar and
 * MatBottomSheet delegate the same way). Nearly every dialog in the app is opened from a lazy module,
 * so the whole fix rests on that third-party detail; it has its own spec so a Material upgrade fails
 * a test instead of silently restoring the bug.
 */
@Injectable({ providedIn: 'root' })
export class OverlayTeardownService {

  /**
   * The dialog refs this service force-closed. A forced close emits {@code afterClosed} with
   * {@code undefined}, indistinguishable from a user dismissal — see {@link wasForceClosed}. Weak,
   * so a closed ref stays collectable.
   */
  private readonly forceClosed = new WeakSet<object>();

  /** Dialogs registered via {@link exemptFromTeardown}. Weak, for the same reason. */
  private readonly exempt = new WeakSet<object>();

  /** The toast registered via {@link exemptSnackBarFromTeardown}, while it is still the open one. */
  private exemptSnackBar: MatSnackBarRef<unknown> | null = null;

  /**
   * The self-clearing subscription on {@link exemptSnackBar}'s {@code afterDismissed}. Held so it can
   * be released with the exemption: under {@code display.toastDuration: 'persistent'} that observable
   * never fires, so nothing else would ever complete it and every notice would leave one behind for
   * the tab's lifetime.
   */
  private exemptSnackBarSub: Subscription | null = null;

  /** One dev-only warning per instance if the overlay services never made it into the injector. */
  private warnedAboutMissingDeps = false;

  constructor(
    @Optional() private dialog: MatDialog | null,
    @Optional() private snackBar: MatSnackBar | null,
    @Optional() private bottomSheet: MatBottomSheet | null,
  ) {}

  /**
   * Dismiss every open overlay. No-op for whichever service isn't available (tests only).
   *
   * <p>Closes each ref individually rather than calling {@code MatDialog.closeAll()} — that helper
   * is all-or-nothing and would take the exempted dialogs with it. Same order Material uses
   * internally (last-opened first). The list is snapshotted first because each close splices its ref
   * out of {@code openDialogs} as it goes.
   *
   * <p><b>This method never throws</b> — each step is {@link contained} individually. It runs as the
   * FIRST statement of every teardown, where an escaping error would abort the caller with the
   * overlays half-closed AND the session never torn down; callers need no try/catch.
   */
  closeAll(reason: OverlayCloseReason = 'session-teardown'): void {
    this.warnIfDepsMissing();
    const open = [...(this.dialog?.openDialogs ?? [])];
    for (let i = open.length - 1; i >= 0; i--) {
      const ref = open[i];
      if (reason === 'session-teardown' && this.exempt.has(ref)) {
        continue;
      }
      // Marked BEFORE the close, and left marked even if it throws: the flag means "this service
      // closed it, not the user", and an afterClosed that already fired must not read as user intent.
      this.forceClosed.add(ref);
      this.contained('dialog close', () => ref.close());
    }
    // ONE-SHOT, and dropped under EITHER reason: the exemption buys the notice exactly the one teardown
    // it was registered against (see exemptSnackBarFromTeardown), never a standing pass. Clearing only
    // on `afterDismissed` was not enough — under `display.toastDuration: 'persistent'` a toast never
    // auto-dismisses, so the ref stayed registered for the tab's whole life and every later closeAll()
    // skipped the snackbar dismissal, leaving unrelated toasts on the login page.
    const exemptToast = this.exemptSnackBar;
    this.clearSnackBarExemption();
    // Skip the dismissal ONLY for a toast that is still the one on screen. `afterDismissed` fires at
    // the END of the exit animation, so a toast displaced by a later one stays registered for ~150ms
    // after it stopped being visible; spending the exemption there would let the toast that REPLACED
    // it ride out the teardown onto the login page — the same "unrelated toast survives" bug the
    // one-shot rule already closed from the other side.
    if (!exemptToast || this.wasDisplaced(exemptToast)) {
      this.contained('snackbar dismiss', () => this.snackBar?.dismiss());
    }
    this.contained('bottom sheet dismiss', () => this.bottomSheet?.dismiss());
  }

  /**
   * Whether {@code ref} has already been replaced by a later toast (or dismissed). Only one snackbar
   * is on screen at a time, and Material tracks it on {@code MatSnackBar._openedSnackBarRef} — public
   * in the typings, and on the ROOT instance it reflects a child's open the same way
   * {@code openDialogs} does.
   *
   * <p>{@code undefined} means "cannot tell" — a spec stub that doesn't carry the property — and is
   * read as NOT displaced, so the exemption behaves as it always did wherever liveness is unknowable.
   * A real MatSnackBar reports {@code null} when nothing is open, which IS displaced (and the
   * dismissal it then allows is a no-op).
   */
  private wasDisplaced(ref: MatSnackBarRef<unknown>): boolean {
    const open = this.snackBar?._openedSnackBarRef;
    return open !== undefined && open !== ref;
  }

  /** Run one teardown step so a failure can't take the remaining ones (or the caller) down with it. */
  private contained(step: string, run: () => void): void {
    try {
      run();
    } catch (err) {
      console.error(`[OverlayTeardownService] ${step} failed; continuing the teardown`, err);
    }
  }

  /**
   * Keep {@code ref} on screen when the session is torn down. <b>Only for a dialog whose content is
   * UNRECOVERABLE once dismissed</b> — the one in-tree consumer is {@code BackupCodesDialogComponent};
   * nothing else should use it, and the exemption is NOT honoured on an {@code 'identity-switch'}
   * close (see CLAUDE.md).
   */
  exemptFromTeardown(ref: MatDialogRef<unknown, unknown>): void {
    this.exempt.add(ref);
  }

  /**
   * Keep {@code ref} — a toast — from being dismissed by {@link closeAll}. <b>Only for a message
   * that EXPLAINS a teardown</b>, which would otherwise be wiped by the very next one.
   *
   * <p>The in-tree consumer is the identity-switch notice, which would otherwise be wiped by the
   * {@code clearSession()} that follows when the freshly-navigated home page 401s.
   *
   * <p><b>The exemption is one-shot, and only ever protects the toast it names.</b> Three guards, all
   * needed (see CLAUDE.md for what each one alone got wrong): it is dropped by the FIRST
   * {@link closeAll} after registration, it self-clears on {@code afterDismissed}, and it skips the
   * dismissal only while this ref is still the toast on screen ({@link wasDisplaced}).
   *
   * <p>Unlike the dialog exemption, this one is honoured on an {@code 'identity-switch'} close too — a
   * toast carries no secret of the outgoing user's.
   */
  exemptSnackBarFromTeardown(ref: MatSnackBarRef<unknown>): void {
    // Release any exemption still registered (a 'persistent' notice whose afterDismissed never fired)
    // before taking its place — one live subscription at a time, never a stack of them.
    this.clearSnackBarExemption();
    this.exemptSnackBar = ref;
    this.exemptSnackBarSub = ref.afterDismissed().subscribe(() => {
      if (this.exemptSnackBar === ref) {
        this.clearSnackBarExemption();
      }
    });
  }

  /** Forget the exempted toast and release the subscription that was watching it. */
  private clearSnackBarExemption(): void {
    this.exemptSnackBar = null;
    this.exemptSnackBarSub?.unsubscribe();
    this.exemptSnackBarSub = null;
  }

  /**
   * <b>The preferred way to consume a dialog result.</b> Like {@code ref.afterClosed()}, but never
   * emits for a close this service forced, so the handler cannot mistake a teardown for user intent.
   * Still completes (Material completes {@code afterClosed} right after its single emission), so
   * {@code take(1)}/`SubSink` on top behave normally.
   *
   * <p>Reach for {@link wasForceClosed} directly ONLY when part of the handler must run on every
   * close — releasing local UI state you own — and only the rest is session-sensitive
   * ({@code transfer-type-filter-derivato-stock} is the in-tree example).
   */
  afterUserClosed<T, R>(ref: MatDialogRef<T, R>): Observable<R | undefined> {
    return ref.afterClosed().pipe(filter(() => !this.wasForceClosed(ref)));
  }

  /**
   * Whether {@code ref} was closed by this service rather than by the user. Prefer
   * {@link afterUserClosed}, which applies this check for you.
   *
   * <p><b>Deliberately reason-agnostic</b> — one boolean, not a reason: every in-tree consumer skips
   * its side effect on both force-close paths (teardown: the call would 401; identity switch: it
   * would succeed under the WRONG user).
   *
   * <p><b>Reads as "this service closed it", not strictly "the user did not"</b> — a teardown landing
   * inside a dialog's ~150ms exit animation marks a close the user really did initiate. Deliberate,
   * pinned by a spec, and reasoned out in CLAUDE.md. A handler that must run its effect on the user's
   * result no matter what should subscribe to {@code ref.afterClosed()} directly.
   */
  wasForceClosed(ref: MatDialogRef<unknown, unknown> | null | undefined): boolean {
    return !!ref && this.forceClosed.has(ref);
  }

  /**
   * A real build ALWAYS has these three (AppModule imports MaterialModule), so a null means the
   * wiring regressed and teardown is silently dismissing nothing — this bug, back and invisible.
   *
   * <p>Latched on the first call (the deps are constructor-injected and cannot change) and keyed on
   * the DIALOG alone (a spec routinely provides just a MatDialog spy). Deliberately NOT gated on
   * {@code environment.production} — under Karma that flag reads {@code true}, so the gate could
   * never fire; why, in CLAUDE.md.
   */
  private warnIfDepsMissing(): void {
    if (this.warnedAboutMissingDeps) {
      return;
    }
    this.warnedAboutMissingDeps = true;
    if (!this.dialog) {
      console.warn(
        '[OverlayTeardownService] MatDialog is missing from the injector — overlay teardown (session' +
        ' teardown and cross-tab identity switch) will not dismiss anything. Expected only in a TestBed' +
        ' that does not import the Material modules.',
        { snackBar: !!this.snackBar, bottomSheet: !!this.bottomSheet },
      );
    }
  }
}
